import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/auth.store';
import { API_BASE_URL, refreshAdminTokensOnce } from './adminAuth';
import { parseApiError } from '../utils/apiError';

export const adminApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

const isAdminUser = () => {
  const role = useAuthStore.getState().user?.role;
  return role === 'platform_admin' || role === 'admin';
};

// Admin Request Interceptor: Attach Admin Bearer JWT Token
adminApi.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore.getState();
    // Only attach admin token if user is an admin and header is not already set
    if (!config.headers.Authorization && accessToken && isAdminUser()) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Endpoints whose 401 means "bad credentials / bad refresh token", not "access token expired".
const NO_REFRESH_PATHS = [
  '/v1/admin/auth/login',
  '/v1/admin/auth/refresh',
  '/v1/admin/auth/logout',
];

const MUST_CHANGE_PASSWORD_MESSAGE = 'Bắt buộc phải đổi mật khẩu';

// Admin Response Interceptor: single-flight token refresh, forced security gates
adminApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const status = error.response?.status;

    if (!originalRequest || !isAdminUser()) {
      return Promise.reject(error);
    }

    if (status === 403) {
      const parsed = parseApiError(error);
      if (parsed.code === 'ADMIN_TOTP_ENROLLMENT_REQUIRED') {
        useAuthStore.getState().setSecurityFlags({ mustEnrollTotp: true });
      } else if (parsed.messages.includes(MUST_CHANGE_PASSWORD_MESSAGE)) {
        useAuthStore.getState().setSecurityFlags({ mustChangePassword: true });
      }
      return Promise.reject(error);
    }

    const url = originalRequest.url || '';
    const skipRefresh = NO_REFRESH_PATHS.some((p) => url.includes(p));

    if (status === 401 && !originalRequest._retry && !skipRefresh) {
      originalRequest._retry = true;
      const authHeader = String(originalRequest.headers.Authorization || '');
      const failedAccessToken = authHeader.startsWith('Bearer ') ? authHeader.slice(7) : null;

      try {
        const tokens = await refreshAdminTokensOnce(failedAccessToken);
        originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;
        return adminApi(originalRequest);
      } catch (refreshError) {
        // 401 "Refresh token không hợp lệ hoặc đã hết hạn" (or any BE rejection) → clear tokens, back to login.
        // A network failure keeps the stored pair: the next 401 simply retries the refresh.
        if (!axios.isAxiosError(refreshError) || refreshError.response) {
          useAuthStore.getState().logout();
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);
